import * as React from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

// Egen accordion med samma API som shadcn/Radix-varianten (type, collapsible,
// defaultValue, value, onValueChange). Skillnaden: innehållet renderas alltid
// och döljs med `hidden` när panelen är stängd. Radix monterar bara innehållet
// i öppna paneler (även med forceMount renderas inga barn), så FAQ-svaren
// fanns tidigare bara i JSON-LD och inte i serverns HTML.

type AccordionContextValue = {
  isOpen: (value: string) => boolean
  toggle: (value: string) => void
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null)
const ItemContext = React.createContext<{ value: string; open: boolean; disabled: boolean; baseId: string } | null>(null)

type CommonProps = Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue" | "dir"> & {
  children?: React.ReactNode
}

type SingleProps = CommonProps & {
  type: "single"
  collapsible?: boolean
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
}

type MultipleProps = CommonProps & {
  type: "multiple"
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
}

type AccordionProps = SingleProps | MultipleProps

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>((props, ref) => {
  const { type, value, defaultValue, onValueChange, children, ...rest } = props as AccordionProps & {
    collapsible?: boolean
  }
  const collapsible = type === "single" ? Boolean((props as SingleProps).collapsible) : true
  delete (rest as { collapsible?: boolean }).collapsible

  const toList = (v: string | string[] | undefined): string[] =>
    v === undefined || v === "" ? [] : Array.isArray(v) ? v : [v]
  const [internal, setInternal] = React.useState<string[]>(() => toList(defaultValue))
  const controlled = value !== undefined
  const open = controlled ? toList(value) : internal

  const ctx = React.useMemo<AccordionContextValue>(
    () => ({
      isOpen: (v) => open.includes(v),
      toggle: (v) => {
        let next: string[]
        if (type === "multiple") {
          next = open.includes(v) ? open.filter((x) => x !== v) : [...open, v]
        } else if (open.includes(v)) {
          if (!collapsible) return
          next = []
        } else {
          next = [v]
        }
        if (!controlled) setInternal(next)
        if (type === "multiple") (onValueChange as ((v: string[]) => void) | undefined)?.(next)
        else (onValueChange as ((v: string) => void) | undefined)?.(next[0] ?? "")
      },
    }),
    [open, type, collapsible, controlled, onValueChange],
  )

  return (
    <AccordionContext.Provider value={ctx}>
      <div ref={ref} data-orientation="vertical" {...rest}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
})
Accordion.displayName = "Accordion"

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string; disabled?: boolean }
>(({ className, value, disabled = false, ...props }, ref) => {
  const ctx = React.useContext(AccordionContext)
  const open = ctx?.isOpen(value) ?? false
  const baseId = React.useId()
  return (
    <ItemContext.Provider value={{ value, open, disabled, baseId }}>
      <div
        ref={ref}
        data-state={open ? "open" : "closed"}
        {...(disabled ? { "data-disabled": "" } : {})}
        className={cn("border-b", className)}
        {...props}
      />
    </ItemContext.Provider>
  )
})
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, onClick, ...props }, ref) => {
  const ctx = React.useContext(AccordionContext)
  const item = React.useContext(ItemContext)
  const open = item?.open ?? false
  return (
    <h3 className="flex" data-state={open ? "open" : "closed"}>
      <button
        ref={ref}
        type="button"
        id={item ? `${item.baseId}-trigger` : undefined}
        aria-controls={item ? `${item.baseId}-content` : undefined}
        aria-expanded={open}
        data-state={open ? "open" : "closed"}
        disabled={item?.disabled}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
          className
        )}
        onClick={(e) => {
          onClick?.(e)
          if (!e.defaultPrevented && item) ctx?.toggle(item.value)
        }}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" aria-hidden="true" />
      </button>
    </h3>
  )
})
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const item = React.useContext(ItemContext)
  const open = item?.open ?? false
  return (
    <div
      ref={ref}
      role="region"
      id={item ? `${item.baseId}-content` : undefined}
      aria-labelledby={item ? `${item.baseId}-trigger` : undefined}
      data-state={open ? "open" : "closed"}
      // Alltid i DOM:en (och i serverns HTML) – stängd panel döljs bara.
      hidden={!open}
      className="overflow-hidden text-sm data-[state=open]:animate-fade-in"
      {...props}
    >
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </div>
  )
})
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
